import React, { useCallback, useState } from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';
import { bindActionCreators } from 'redux';

import feUpdateTimeOffCategories from 'actions/timeoff/feUpdateTimeOffCategories.action';
import TimeOffCategoriesHeader from 'components/hr/categories/TimeOffCategoriesHeader.component';
import TimeOffCategory from 'components/hr/categories/TimeOffCategory.component';
import DELAY from 'constants/delay.const';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { selectCurrentSalonId } from 'selectors/currentSalon.selectors';
import { selectTimeOffCategories } from 'selectors/timeoff.selectors';
import asArray from 'utils/arrays/asArray.util';
import delayed from 'utils/functions/delayed.util';
import alwaysSame from 'utils/functions/nullary/alwaysSame.util';
import C from 'utils/values/constant.util';

import CN from './TimeOffCategories.module.scss';

const ALERT_CLASSES = 'alert centered animated fadeInDown';

const INITIAL_ALERT = C({
  message: '',
  type: '',
});

const DEFAULT_CATEGORY = C({
  defaultAllowance: null,
  defaultAllowanceHours: null,
  color: '#f3f3f3',
  isDefault: false,
  name: '',
  nonWorkingHours: false,
});

const TimeOffCategories = ({ salonId, categories: categoriesFromProps, feUpdateTimeOffCategories }) => {
  const { t } = useTranslation();
  const categories = asArray(categoriesFromProps);
  const [categoryId, setCategoryId] = useState(0);
  const { val: isAlertShown, yay: showAlert, nay: hideAlert } = useTip(false);
  const [alertInfo, setAlertInfo] = useState(INITIAL_ALERT);

  const handleClick = useCallback(() => {
    const initialCategory = C({ ...DEFAULT_CATEGORY, id: `tempCat+${categoryId}`, salonId });

    feUpdateTimeOffCategories(initialCategory);
    setCategoryId(categoryId + 1);
  }, [categoryId, feUpdateTimeOffCategories, salonId]);

  const handleAlert = useCallback(
    ({ type, message }) => {
      showAlert();
      setAlertInfo({ type, message });

      delayed(DELAY.autohide, () => {
        hideAlert();
        setAlertInfo(INITIAL_ALERT);
      });
    },
    [hideAlert, showAlert],
  );

  return (
    <div data-bem="TimeOffCategories">
      <div className={CN.inner}>
        <TimeOffCategoriesHeader />

        <div data-bem="TimeOffCategories__list" className={CN.container}>
          {categories.filter(alwaysSame).map((category) => (
            <TimeOffCategory key={category.id} category={category} onAlert={handleAlert} />
          ))}
        </div>
      </div>

      <button className="btn btn-blue" onClick={handleClick}>
        {t('humanResources.timeOffSettings.categories.addCategoryButton')}
      </button>

      {isAlertShown && <div className={cn(ALERT_CLASSES, alertInfo.type)}>{alertInfo.message}</div>}
    </div>
  );
};

const mapStateToProps = (state) => ({
  salonId: selectCurrentSalonId(state),
  categories: selectTimeOffCategories(state),
});

const mapDispatchToProps = (dispatch) => bindActionCreators({ feUpdateTimeOffCategories }, dispatch);

const TimeOffCategoriesHoc = connect(mapStateToProps, mapDispatchToProps)(TimeOffCategories);

export default TimeOffCategoriesHoc;
